import { useEffect, useMemo, useState } from 'react';
import type { Resource } from '../lib/types';
import { buildCalibrationReport } from '../lib/calibration';
import { addDays, parseIsoDate } from '../lib/format';
import { NotAuthenticatedError, api } from '../lib/api';

/** Horizon options. Quarters, because that's how calibration is planned. */
const HORIZONS: [number, string][] = [
  [4, '1 year'],
  [8, '2 years'],
];

type Focus = 'all' | 'next30' | 'overdue';

function daysBetween(fromIso: string, toIso: string): number {
  return Math.round(
    (parseIsoDate(toIso).getTime() - parseIsoDate(fromIso).getTime()) / 86_400_000,
  );
}

/** 'in 12 days' / '34 days ago' — the number people actually act on. */
function relativeDue(today: string, due: string): string {
  const d = daysBetween(today, due);
  if (d === 0) return 'today';
  if (d > 0) return `in ${d} day${d === 1 ? '' : 's'}`;
  return `${-d} day${d === -1 ? '' : 's'} ago`;
}

/**
 * What's due for calibration, by quarter.
 *
 * Reads the catalogue the page already has — no report endpoint. The
 * horizon starts at the current quarter and runs 1 or 2 years out;
 * overdue and untracked items sit outside it and are always shown,
 * since hiding a past-due instrument behind a date filter is exactly
 * the failure this report exists to prevent.
 */
export function CalibrationReport({
  today,
  onSignedOut,
}: {
  today: string;
  onSignedOut: () => void;
}) {
  const [quarters, setQuarters] = useState(4);
  const [includeRetired, setIncludeRetired] = useState(false);
  const [focus, setFocus] = useState<Focus>('all');

  // Loads its own copy of the catalogue rather than reusing the page's.
  // The shared list is filtered to bookable resources unless the user
  // has flipped "include retired"; a calibration report that silently
  // omitted an out-of-service instrument would be worse than no report.
  const [resources, setResources] = useState<Resource[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .listResources(true)
      .then((rows) => {
        if (!cancelled) setResources(rows);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof NotAuthenticatedError) onSignedOut();
        else setLoadError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [onSignedOut]);

  const report = useMemo(
    () =>
      buildCalibrationReport(resources ?? [], {
        today,
        quarters,
        includeRetired,
      }),
    [resources, today, quarters, includeRetired],
  );

  const next30Cutoff = addDays(today, 30);
  const next30Keys = useMemo(
    () => new Set(report.dueNext30.map((r) => r.id)),
    [report],
  );

  const buckets = report.buckets
    .map((b) => ({
      ...b,
      items: b.items.filter((r) => {
        if (focus === 'overdue') return b.overdue === true;
        if (focus === 'next30') return next30Keys.has(r.id);
        return true;
      }),
    }))
    .filter((b) => b.items.length > 0 || focus === 'all');

  const tile = (
    label: string,
    value: number,
    key: Focus,
    colors: { bg: string; fg: string; bd: string },
  ) => (
    <button
      onClick={() => setFocus(focus === key ? 'all' : key)}
      className="kpi"
      style={{
        background: colors.bg,
        border: `0.5px solid ${colors.bd}`,
        textAlign: 'left',
        outline: focus === key ? `2px solid ${colors.bd}` : 'none',
        cursor: 'pointer',
      }}
    >
      <div className="label" style={{ color: colors.fg }}>
        {label}
      </div>
      <div className="value" style={{ color: colors.fg }}>
        {value}
      </div>
    </button>
  );

  if (loadError !== null) {
    return <div className="error-banner">Failed to load report: {loadError}</div>;
  }
  if (resources === null) {
    return <div className="empty">Loading calibration schedule…</div>;
  }

  return (
    <div>
      <div
        className="toolbar"
        style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
      >
        <div style={{ display: 'flex', gap: 4 }}>
          {HORIZONS.map(([n, label]) => (
            <button
              key={n}
              onClick={() => setQuarters(n)}
              className="tab-btn"
              style={{
                background:
                  quarters === n ? 'var(--blue-bg)' : 'var(--bg-secondary)',
                color: quarters === n ? 'var(--blue-fg)' : 'var(--text-secondary)',
                border: `0.5px solid ${
                  quarters === n ? 'var(--blue-bd)' : 'var(--border-md)'
                }`,
                borderRadius: 6,
                fontSize: 12,
                padding: '3px 10px',
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <label
          style={{
            fontSize: 12,
            color: 'var(--text-secondary)',
            display: 'flex',
            alignItems: 'center',
            gap: 5,
          }}
        >
          <input
            type="checkbox"
            checked={includeRetired}
            onChange={(e) => setIncludeRetired(e.target.checked)}
          />
          Include retired
        </label>
        <span className="spacer" style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
          {report.totalTracked} tracked
          {report.beyondHorizon > 0 && ` · ${report.beyondHorizon} beyond ${
            quarters === 4 ? '1 year' : '2 years'
          }`}
        </span>
      </div>

      <div className="kpis" style={{ gridTemplateColumns: 'repeat(3,1fr)' }}>
        {tile('Overdue', report.overdue.length, 'overdue', {
          bg: 'var(--red-bg)',
          fg: 'var(--red-fg)',
          bd: 'var(--red-bd)',
        })}
        {tile('Due in next 30 days', report.dueNext30.length, 'next30', {
          bg: 'var(--amber-bg)',
          fg: 'var(--amber-fg)',
          bd: 'var(--amber-bd)',
        })}
        {tile('Tracked instruments', report.totalTracked, 'all', {
          bg: 'var(--bg-secondary)',
          fg: 'var(--text-primary)',
          bd: 'var(--border-md)',
        })}
      </div>

      {focus !== 'all' && (
        <div
          style={{
            fontSize: 11,
            color: 'var(--text-secondary)',
            marginBottom: 10,
          }}
        >
          Filtered to {focus === 'overdue' ? 'overdue' : 'the next 30 days'} —
          click the tile again to show everything.
        </div>
      )}

      {buckets.every((b) => b.items.length === 0) ? (
        <div className="empty">Nothing due in this range.</div>
      ) : (
        buckets.map((bucket) => (
          <div key={bucket.key} style={{ marginBottom: 16 }}>
            <div
              className="section-title"
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 8,
                color: bucket.overdue ? 'var(--red-fg)' : 'var(--text-secondary)',
              }}
            >
              <span style={{ fontWeight: 600 }}>{bucket.label}</span>
              <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                {bucket.detail}
              </span>
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: 11 }}>
                {bucket.items.length} item{bucket.items.length === 1 ? '' : 's'}
              </span>
            </div>

            {bucket.items.length === 0 ? (
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--text-tertiary)',
                  padding: '6px 2px',
                }}
              >
                None.
              </div>
            ) : (
              <div className="table">
                <div
                  className="table-head"
                  style={{ gridTemplateColumns: '1.2fr 2fr 1fr 1fr 1fr' }}
                >
                  <span>Asset tag</span>
                  <span>Name</span>
                  <span>Location</span>
                  <span>Due</span>
                  <span>When</span>
                </div>
                {bucket.items.map((r) => {
                  const soon =
                    !bucket.overdue && r.nextDue !== '' && r.nextDue <= next30Cutoff;
                  return (
                    <div
                      key={r.id}
                      className="table-row"
                      style={{
                        gridTemplateColumns: '1.2fr 2fr 1fr 1fr 1fr',
                        cursor: 'default',
                      }}
                    >
                      <span style={{ fontSize: 12 }}>{r.title}</span>
                      <span style={{ fontSize: 12 }}>
                        {r.name}
                        {!r.isActive && (
                          <span
                            style={{
                              fontSize: 10,
                              color: 'var(--text-tertiary)',
                              marginLeft: 6,
                            }}
                          >
                            retired
                          </span>
                        )}
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {r.office || '—'}
                      </span>
                      <span style={{ fontSize: 12 }}>{r.nextDue || '—'}</span>
                      <span
                        style={{
                          fontSize: 11,
                          color: bucket.overdue
                            ? 'var(--red-fg)'
                            : soon
                              ? 'var(--amber-fg)'
                              : 'var(--text-secondary)',
                          fontWeight: bucket.overdue || soon ? 500 : 400,
                        }}
                      >
                        {r.nextDue ? relativeDue(today, r.nextDue) : 'not scheduled'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
